import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Car, Archive, Search } from "lucide-react";
import { ComboBoxCreatable } from "@/components/ComboBoxCreatable";
import { COLORS, mergedOptions } from "@/lib/vehicleCatalog";
import { ColorChip } from "@/components/ColorChip";

const FUEL = [
  { v: "", label: "—" },
  { v: "benzin", label: "Benzin" },
  { v: "diesel", label: "Diesel" },
  { v: "elektro", label: "Elektro" },
  { v: "hybrid", label: "Hybrid" },
  { v: "lpg", label: "LPG/CNG" },
  { v: "other", label: "Sonstige" },
];

const BODY = [
  { v: "", label: "—" },
  { v: "limousine", label: "Limousine" },
  { v: "kombi", label: "Kombi" },
  { v: "suv", label: "SUV" },
  { v: "coupe", label: "Coupé" },
  { v: "cabrio", label: "Cabrio" },
  { v: "van", label: "Van/Transporter" },
  { v: "pickup", label: "Pickup" },
  { v: "sonstige", label: "Sonstige" },
];

const STATUS_LABEL = {
  active: { label: "Aktiv", cls: "border border-emerald-200 bg-emerald-50 text-emerald-700" },
  in_workshop: { label: "In der Werkstatt", cls: "border border-orange-200 bg-orange-50 text-orange-700" },
  delivered: { label: "Ausgeliefert", cls: "border border-blue-200 bg-blue-50 text-blue-700" },
  archived: { label: "Archiviert", cls: "border border-slate-200 bg-slate-100 text-slate-600" },
};

const emptyForm = { customer_id: "", manufacturer: "", model: "", year: "", license_plate: "", vin: "", color: "", mileage: "", fuel_type: "", body_type: "", notes: "", engine: "", internal_reference: "", registration_date: "" };

export function VehicleForm({ form, setForm, customers, disableCustomer = false, readOnlyOwner = null }) {
  const [showMore, setShowMore] = useState(false);
  const setF = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="space-y-3">
      {readOnlyOwner ? (
        <div>
          <Label>Kunde</Label>
          <div
            className="flex h-9 items-center rounded-md border border-input bg-muted/40 px-3 text-sm"
            data-testid="vehicle-owner-readonly"
            title="Der Fahrzeughalter kann hier nicht geändert werden."
          >
            <Link
              to={`/kunden/${readOnlyOwner.id}`}
              className="truncate text-primary hover:underline"
              data-testid="vehicle-owner-link"
            >
              {readOnlyOwner.name || "—"}
            </Link>
          </div>
        </div>
      ) : (!disableCustomer && (
        <div>
          <Label>Kunde *</Label>
          <Select value={form.customer_id || ""} onValueChange={(v) => setF("customer_id", v)}>
            <SelectTrigger data-testid="vehicle-customer"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
            <SelectContent>
              {customers.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ))}
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Hersteller *</Label><Input value={form.manufacturer} onChange={(e) => setF("manufacturer", e.target.value)} placeholder="BMW" data-testid="vehicle-manufacturer" /></div>
        <div><Label>Modell *</Label><Input value={form.model} onChange={(e) => setF("model", e.target.value)} placeholder="320d" data-testid="vehicle-model" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Farbe</Label>
          <ComboBoxCreatable
            testId="vehicle-color"
            value={form.color}
            onChange={(v) => setF("color", v)}
            options={mergedOptions(COLORS, "color")}
            placeholder="Farbe wählen…"
            renderValue={(v) => <ColorChip value={v} />}
            renderOption={(o) => <ColorChip value={o.value} />}
          />
        </div>
        <div><Label>Kennzeichen (optional)</Label><Input value={form.license_plate} onChange={(e) => setF("license_plate", e.target.value.toUpperCase())} placeholder="F-AB 1234" data-testid="vehicle-plate" /></div>
      </div>
      <div><Label>Notizen (optional)</Label><Textarea rows={2} value={form.notes} onChange={(e) => setF("notes", e.target.value)} placeholder="z.B. Weicher Lack, Kratzer hinten links…" data-testid="vehicle-notes" /></div>

      <button
        type="button"
        onClick={() => setShowMore((s) => !s)}
        data-testid="vehicle-more-toggle"
        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
      >
        {showMore ? "▾" : "▸"} Weitere Angaben {showMore ? "ausblenden" : "einblenden"}
      </button>

      {showMore && (
        <div className="rounded-md border border-dashed border-border/60 p-3 space-y-3" data-testid="vehicle-more-section">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Baujahr</Label><Input type="number" value={form.year} onChange={(e) => setF("year", e.target.value)} placeholder="2019" data-testid="vehicle-year" /></div>
            <div><Label>Kilometerstand</Label><Input type="number" value={form.mileage} onChange={(e) => setF("mileage", e.target.value)} /></div>
          </div>
          <div><Label>VIN (Fahrgestellnummer)</Label><Input value={form.vin} onChange={(e) => setF("vin", e.target.value.toUpperCase())} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Kraftstoff</Label>
              <Select value={form.fuel_type || "none"} onValueChange={(v) => setF("fuel_type", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{FUEL.map(f => <SelectItem key={f.v || "none"} value={f.v || "none"}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Karosserie</Label>
              <Select value={form.body_type || "none"} onValueChange={(v) => setF("body_type", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{BODY.map(b => <SelectItem key={b.v || "none"} value={b.v || "none"}>{b.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Motor</Label><Input value={form.engine} onChange={(e) => setF("engine", e.target.value)} placeholder="2.0 TDI" /></div>
          <div><Label>Interne Referenz</Label><Input value={form.internal_reference} onChange={(e) => setF("internal_reference", e.target.value)} /></div>
        </div>
      )}
    </div>
  );
}

export default function Vehicles() {
  const [q, setQ] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const qc = useQueryClient();

  const { data: vehicles = [] } = useQuery({
    queryKey: ["vehicles", q, includeArchived],
    queryFn: async () => (await api.get("/vehicles", { params: { q, include_archived: includeArchived }})).data,
  });

  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => (await api.get("/customers")).data,
  });

  const createMut = useMutation({
    mutationFn: async () => (await api.post("/vehicles", {
      ...form,
      year: form.year ? Number(form.year) : null,
      mileage: form.mileage ? Number(form.mileage) : null,
      registration_date: form.registration_date || null,
    })).data,
    onSuccess: () => {
      toast.success("Fahrzeug hinzugefügt");
      qc.invalidateQueries({ queryKey: ["vehicles"] });
      setOpen(false); setForm(emptyForm);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div className="stellar-page">
      <div className="stellar-page-heading flex flex-wrap items-center gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="stellar-module-icon stellar-module-indigo"><Car /></div>
            <h1 className="font-display text-2xl sm:text-4xl font-bold tracking-tight">Fahrzeuge</h1>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Alle Fahrzeuge Ihrer Kunden im Überblick.</p>
        </div>
        <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap items-center gap-2">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Suchen…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 w-full sm:w-64 bg-white" data-testid="vehicle-search" />
          </div>
          <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} data-testid="include-archived" />
            Archiv anzeigen
          </label>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-vehicle-btn" size="sm" className="sm:size-default rounded-xl bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline"> Neues Fahrzeug</span></Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Neues Fahrzeug</DialogTitle></DialogHeader>
              <VehicleForm form={form} setForm={setForm} customers={customers} />
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
                <Button onClick={() => createMut.mutate()} disabled={!form.customer_id || !form.manufacturer || !form.model || createMut.isPending} data-testid="save-vehicle">
                  {createMut.isPending ? "Speichern…" : "Speichern"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="premium-card overflow-hidden">
        <table className="stellar-data-table w-full text-sm">
          <thead className="bg-slate-50/80 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Hersteller / Modell</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Kennzeichen</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Kunde</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Status</th>
              <th className="text-left px-4 py-3 hidden xl:table-cell">Letzte Änderung</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {vehicles.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-16 text-sm text-muted-foreground">
                  <Car className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <div className="mb-1">Noch keine Fahrzeuge angelegt.</div>
                  <div className="text-xs">Klicken Sie auf &bdquo;Neues Fahrzeug&ldquo;, um zu beginnen.</div>
                </td>
              </tr>
            )}
            {vehicles.map((v) => {
              const st = STATUS_LABEL[v.status] || STATUS_LABEL.active;
              return (
                <tr key={v.id} className="stellar-table-row">
                  <td className="px-4 py-3">
                    <Link to={`/fahrzeuge/${v.id}`} data-testid={`vehicle-row-${v.id}`} className="font-medium hover:underline flex items-center gap-2">
                      {v.status === "archived" && <Archive className="h-3.5 w-3.5 text-muted-foreground" />}
                      {v.manufacturer} {v.model} <span className="font-normal text-muted-foreground">{v.year && `· Bj. ${v.year}`}</span>{v.color && <span className="ml-1 font-normal text-muted-foreground">· <ColorChip value={v.color} /></span>}
                    </Link>
                    <div className="text-xs text-muted-foreground md:hidden">{v.year && `Bj. ${v.year} · `}{v.license_plate}{v.color && ` · ${v.color}`} · {v.customer_name}</div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell font-mono text-xs">{v.license_plate || "—"}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">{v.customer_name}</td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-semibold ${st.cls}`}>{st.label}</span>
                  </td>
                  <td className="px-4 py-3 hidden xl:table-cell text-muted-foreground text-xs">
                    {new Date(v.updated_at).toLocaleDateString("de-DE")}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
